import { useState, useRef, useEffect, useCallback } from 'react';
import '../../styles/Admin.css';
import '../../styles/GaleriaAdmin.css';
import { useAuth } from '../../context/AuthContext.jsx';
import { GALLERY_CATEGORIES } from '../../data/galeria.js';
import { SHORT_FILMS } from '../../data/galeria.js';

const CATEGORY_LABELS = { main: 'Main Board', new: 'Nuevos Rostros', lifestyle: 'Lifestyle', influencer: 'Influencers' };

function GaleriaAdminPage() {
  const { getAuthHeader } = useAuth();
  const [activeSection, setActiveSection] = useState('modelos');
  const [activeCategory, setActiveCategory] = useState('all');
  const [cloudModels, setCloudModels] = useState([]);
  const [cloudShortFilms, setCloudShortFilms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const fileInputRef = useRef(null);

  const fetchGallery = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/gallery');
      if (!res.ok) throw new Error('Error al cargar la galería');
      const data = await res.json();
      setCloudModels(data.models || []);
      setCloudShortFilms(data.shortFilms || []);
    } catch (e) {
      setError(e.message || 'Error de red');
      setCloudModels([]);
      setCloudShortFilms([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  const categoryModels =
    activeCategory === 'all' ? cloudModels : cloudModels.filter((m) => m.category === activeCategory);

  const toggleModelo = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddPhotosClick = () => {
    fileInputRef.current?.click();
  };

  const processFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith('image/'));
    if (list.length === 0) return;
    setUploading(true);
    setError('');
    const headers = { 'Content-Type': 'application/json', ...getAuthHeader() };
    for (const file of list) {
      try {
        const presignRes = await fetch('/api/gallery/presign', {
          method: 'POST',
          headers,
          body: JSON.stringify({ filename: file.name || 'image.jpg', type: 'modelo', contentType: file.type || undefined }),
        });
        if (!presignRes.ok) {
          const data = await presignRes.json().catch(() => ({}));
          throw new Error(data.error || 'No fue posible preparar la subida');
        }

        const presignData = await presignRes.json();

        const uploadRes = await fetch(presignData.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': presignData.contentType || file.type || 'application/octet-stream' },
          body: file,
        });
        if (!uploadRes.ok) {
          throw new Error('Error al subir archivo a almacenamiento');
        }

        const commitRes = await fetch('/api/gallery/commit', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            key: presignData.key,
            id: presignData.id,
            filename: file.name || 'image.jpg',
            type: presignData.type || 'modelo',
          }),
        });
        if (!commitRes.ok) {
          const data = await commitRes.json().catch(() => ({}));
          throw new Error(data.error || 'Error al subir');
        }
      } catch (e) {
        setError(e.message);
      }
    }
    setUploading(false);
    fetchGallery();
    if (list.length > 0) {
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }
  };

  const handleFileChange = (e) => {
    processFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    processFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleRemoveSelected = async () => {
    if (activeSection === 'modelos' && selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    setError('');
    const headers = getAuthHeader();
    for (const id of ids) {
      try {
        const res = await fetch(`/api/galleryItem?type=modelo&id=${encodeURIComponent(id)}`, {
          method: 'DELETE',
          headers,
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Error al eliminar');
        }
      } catch (e) {
        setError(e.message);
        break;
      }
    }
    setSelectedIds(new Set());
    fetchGallery();
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const hasSelectedInCurrentSection = activeSection === 'modelos' ? selectedIds.size > 0 : false;

  const handleSave = () => {
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const displayShortFilms = cloudShortFilms.length > 0 ? cloudShortFilms : SHORT_FILMS;

  return (
    <div className="admin-main admin-gallery-editor" style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', background: 'var(--admin-bg)' }}>
      {showToast && (
        <div className="admin-toast">
          <span className="material-symbols-outlined" style={{ color: 'var(--admin-primary)', fontSize: '1.25rem' }}>check_circle</span>
          <span style={{ fontSize: '10px', letterSpacing: '0.2em', fontWeight: 700, textTransform: 'uppercase' }}>Listo</span>
        </div>
      )}

      <header className="admin-gallery-editor-header">
        <div className="admin-gallery-editor-header-left">
          <span className="material-symbols-outlined admin-gallery-editor-icon" style={{ fontSize: '1.5rem' }}>photo_library</span>
          <h2 className="admin-gallery-editor-title">Editor de Galería</h2>
        </div>
        <div className="admin-gallery-editor-header-actions">
          <button
            type="button"
            className="admin-gallery-editor-add-btn"
            onClick={handleRemoveSelected}
            disabled={!hasSelectedInCurrentSection || uploading}
            aria-label="Eliminar elementos seleccionados"
          >
            <span className="material-symbols-outlined">delete</span>
            <span>Eliminar elementos seleccionados</span>
          </button>
          <button type="button" className="admin-gallery-editor-add-btn" onClick={handleAddPhotosClick} disabled={uploading} aria-label="Añadir nueva foto">
            <span className="material-symbols-outlined">add</span>
            <span>{uploading ? 'Subiendo...' : 'Añadir Nueva Foto'}</span>
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          className="admin-gallery-editor-file-input"
          onChange={handleFileChange}
          aria-hidden
        />
      </header>

      {error && (
        <div className="admin-login-error" style={{ margin: '0 2rem 1rem' }} role="alert">
          {error}
        </div>
      )}

      <main className="admin-gallery-editor-main no-scrollbar">
        <div
          className="admin-gallery-editor-upload"
          role="button"
          tabIndex={0}
          onClick={handleAddPhotosClick}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleAddPhotosClick(); } }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          aria-label="Arrastra tus archivos aquí o haz clic para subir fotos a la galería"
        >
          <span className="material-symbols-outlined">cloud_upload</span>
          <p className="admin-gallery-editor-upload-title">Arrastra tus archivos aquí o haz clic para subir</p>
          <p className="admin-gallery-editor-upload-sub">Soporta formatos JPG, PNG y TIFF de alta resolución. La categoría se asigna por el nombre del archivo (ej. main_01.jpg, lifestyle_foto.png).</p>
          <button type="button" className="admin-gallery-editor-upload-btn" onClick={(e) => { e.stopPropagation(); handleAddPhotosClick(); }}>
            Seleccionar Archivos
          </button>
        </div>

        <div className="admin-gallery-editor-bar">
          <div className="admin-gallery-editor-pills">
            {['Modelos', 'Cortometrajes'].map((label) => (
              <button
                key={label}
                type="button"
                className={`admin-gallery-editor-pill ${activeSection === (label === 'Modelos' ? 'modelos' : 'shortfilms') ? 'active' : ''}`}
                onClick={() => setActiveSection(label === 'Modelos' ? 'modelos' : 'shortfilms')}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {activeSection === 'modelos' && (
          <>
            <div className="admin-gallery-editor-bar">
              <div className="admin-gallery-editor-pills">
                {GALLERY_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    className={`admin-gallery-editor-pill ${activeCategory === cat.id ? 'active' : ''}`}
                    onClick={() => setActiveCategory(cat.id)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
              <span className="admin-gallery-editor-count">{categoryModels.length} elementos mostrados</span>
            </div>

            {loading ? (
              <p style={{ padding: '2rem', color: 'rgba(255,255,255,0.6)' }}>Cargando galería...</p>
            ) : (
              <div className="admin-gallery-editor-grid">
                {categoryModels.map((model) => (
                  <button
                    key={model.id}
                    type="button"
                    className={`admin-gallery-editor-card ${selectedIds.has(model.id) ? 'selected' : ''}`}
                    onClick={() => toggleModelo(model.id)}
                    aria-label={selectedIds.has(model.id) ? `Deseleccionar ${model.name}` : `Seleccionar ${model.name} para eliminar`}
                  >
                    <div className="admin-gallery-editor-card-bg" style={{ backgroundImage: `url(${model.src})` }} />
                    <div className="admin-gallery-editor-card-overlay">
                      <span className="material-symbols-outlined" style={{ fontSize: '2rem', color: selectedIds.has(model.id) ? 'var(--admin-primary)' : 'rgba(255,255,255,0.6)' }}>
                        {selectedIds.has(model.id) ? 'check_circle' : 'radio_button_unchecked'}
                      </span>
                      <span className="admin-gallery-editor-card-cat">{CATEGORY_LABELS[model.category] || model.category}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {activeSection === 'shortfilms' && (
          <>
            <div className="admin-gallery-editor-bar">
              <span className="admin-gallery-editor-count">{displayShortFilms.length} elementos mostrados (desde API o estáticos)</span>
            </div>
            <div className="admin-gallery-editor-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
              {displayShortFilms.map((film) => (
                <div key={film.id} className="admin-gallery-editor-card" style={{ cursor: 'default' }}>
                  <div className="admin-gallery-editor-card-bg" style={{ backgroundImage: `url(${film.thumbnail})` }} />
                  <div className="admin-gallery-editor-card-overlay">
                    <span className="admin-gallery-editor-card-cat">{film.title}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      <footer className="admin-gallery-editor-footer">
        <p className="admin-gallery-editor-unsaved">Los cambios en la galería se reflejan en el sitio público al instante.</p>
        <button type="button" className="admin-gallery-editor-save" onClick={handleSave}>
          Actualizar vista
        </button>
      </footer>
    </div>
  );
}

export default GaleriaAdminPage;
